import { useRouter } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  trackFormSubmission,
  trackGAEvent,
  trackGAEventWithCommunityAndUser
} from '@/utility/analytics';
import {
  LIB_COURSE_ADD_FILE_EVENT,
  LIB_COURSE_ADD_VIDEO_EVENT,
  SESSION_CLICKED
} from '@/utility/analyticsConsts';
import { t } from '@/utility/localization';
import { validatePriceInputs } from '@/utility/priceValidationUtil';
import {
  CM_PORTAL_ADD_NEW_SESSION,
  getDigitalProductAccessPageLink
} from '@/utility/routesHelper';
import { validate } from '@/utility/validation';

import { get1on1SessionManagementUrl } from '@/features/1on1Sessions/utils';

import {
  checkCommunityStorageService,
  createCommunityFolderItem,
  deleteCommunityFolderItem,
  fetchSignedUrlForFolderItem,
  getCommunityFolderItems,
  getSignedURLForASingleFilePart,
  mergeSignedUrlForFolderItemToS3,
  updateCommunityFolderItem,
  updateCommunityFolderItemsOrder,
  uploadFileChunksToS3
} from '@/services/community/communitiesLibraryService';

import { showConfetti } from '@/components/common/ConfettiContainer';
import {
  showErrorToast,
  showSuccessToast,
  showToast
} from '@/components/common/ToastContainer';
import NPLButton from '@/components/npl/NPLButton';

import useLogEvent from '@/hooks/useLogEvent';
import useQueryParams from '@/hooks/useQueryParams';

import { SECTION_FILES_DROPPABLE_ID_PREFIX } from '@/pages/portal/products/components/v3/FolderItem/FolderItemSectionCard';
import {
  ROOT_FILES_DROPPABLE_ID,
  SECTION_DROPPABLE_ID
} from '@/pages/portal/products/components/v3/FolderItemsDropContext';
import {
  FOLDER_ITEM_TYPE_SECTION,
  PRODUCT_CONTENT_TAB,
  STATUS_PUBLISHED,
  STATUS_UNPUBLISHED,
  TAB_COURSES
} from '@/pages/portal/products/constants';
import useFolderData from '@/pages/portal/products/hooks/useFolderData';
import usePollProcessingFiles from '@/pages/portal/products/hooks/usePollProcessingFiles';
import { getLibraryPageUrl } from '@/pages/portal/products/utils';

import { useAuthContext } from '../AuthContext';
import { useCommunityPortalContext } from '../CommunityPortalContext';
import {
  FILE_STATUS,
  FILE_TYPE,
  FOLDER_EVENT,
  MIN_CHUNK_SIZE,
  PRODUCT_TYPE,
  STATUS,
  acceptedTypes,
  addIndexAndClientIdToFolderItems,
  addIndexAndClientIdToFolderItemsRecursively,
  calculateTotalNumberOfChunks,
  exists,
  getDefaultCreateFolderData,
  getFolderItemDataFormatted,
  getFolderParams,
  getFolderValidationSchema,
  getFormattedFileParams,
  getFormattedLogTextForMaxFilesUpload,
  getFormattedTextForFileItemsToBeUploaded,
  getFormattedTextForFileNotSupported,
  getPayloadForSignedURL,
  imageTypes,
  isFileSupported,
  reorder,
  videoTypes
} from './constants';

export const LibraryContext = createContext();

export const LibrarySetupContextProvider = ({ children }) => {
  const router = useRouter();
  const {
    activeCommunity,
    refreshTaskProgressPerc,
    verifyNasioFeePreferenceIsSet
  } = useCommunityPortalContext();
  const activeCommunityId = activeCommunity?._id;

  const logEvent = useLogEvent();

  const {
    folderList,
    getFolderList,
    isLoading: isFolderExecutionLoading,
    setIsLoading,
    classFolderList,
    setFolderList,
    searchTerm,
    sortState,
    setSortState,
    filteredFolderList,
    selectSortState,
    setSearchTerm,
    collectionFolderList,
    createFolder,
    editFolder,
    deleteFolder,
    updatePublishFolder,
    getFolderDetails,
    isFolderItemsLoading,
    isFolderListLoading,
    updateFolderOrder
  } = useFolderData({
    communityId: activeCommunityId
  });

  const [allFolderItemsList, setFolderItemsList] = useState([]);
  const [selectedFolderItem, setSelectedFolderItem] = useState({});
  const [isFolderItemModalOpen, setIsFolderItemModalOpen] =
    useState(false);
  const [showAddContentDiv, setShowAddContentDiv] = useState(false);
  const [activeTabId, setActiveTabId] = useState(TAB_COURSES);
  const [isFolderReorderModalOpen, setIsFolderReorderModalOpen] =
    useState(false);
  const [
    isFolderReorderDiscardModalOpen,
    setIsFolderReorderDiscardModalOpen
  ] = useState(false);
  const [isFolderCreationModalOpen, setIsClassCreationModalOpen] =
    useState(false);
  const [isClassConfirmationModalOpen, setIsFolderConfirmationModalOpen] =
    useState(null);
  const [isFolderDeleteModalOpen, setIsFolderDeleteModalOpen] =
    useState(false);
  const [isFolderItemDeleteModalOpen, setIsFolderItemDeleteModalOpen] =
    useState(false);
  const [isConfirmBackModalOpen, setIsConfirmBackModalOpen] =
    useState(false);
  // const [selectedFolderItemId, setSelectedFolderItemId] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [activeFolderItemTabId, setActiveFolderItemTabId] = useState(
    PRODUCT_CONTENT_TAB
  );
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [currentFolderState, setCurrentFolderState] = useState(null);
  const [folderFormErrors, setFolderFormErrors] = useState({});
  // to set if creation modal is paid or free by default
  const [paidModalSelected, setPaidModalSelected] = useState(false);
  // show save button state
  const [showSaveChangesOption, setShowSaveChangesOption] =
    useState(false);
  const [filesErrorList, setFilesErrorList] = useState([]);
  const [filesUploadingStatus, setFilesUploadingStatus] = useState({});
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadProgressMap, setUploadProgressMap] = useState({});
  const [throttledUploadingProgressMap, setThrottledUploadingProgressMap] =
    useState({});
  const [currency, setCurrency] = useState('USD');

  const [isThrottledProgress, setIsThrottledProgress] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [timeStarted, setTimeStarted] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [totalFileSize, setTotalFileSize] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shortUrl, setShortUrl] = useState('');
  const [folderItemIdsInFocus, setFolderItemIdsInFocus] = useState([]);
  const { user } = useAuthContext();

  const { currentRouterQuery } = useQueryParams();
  const { tab, folderId } = currentRouterQuery || {};

  const [
    selectedEventForDiscountCreation,
    setSelectedEventForDiscountCreation
  ] = useState(null);
  const [showDiscountCreationModal, setShowDiscountCreationModal] =
    useState(false);
  const [
    showDiscountCreationModalFromDropdown,
    setShowDiscountCreationModalFromDropdown
  ] = useState(false);
  const [showDeleteDiscountModal, setShowDeleteDiscountModal] =
    useState(false);
  const [selectedDiscountForDeletion, setSelectedDiscountForDeletion] =
    useState(null);

  // Poll folder items that are processing.
  usePollProcessingFiles({
    communityId: activeCommunityId,
    folderItemsList: allFolderItemsList,
    setFolderItemsList
  });

  const { sectionFolderItemsList, filesFolderItemsList, isFolderEmpty } =
    useMemo(() => {
      let sectionFolderItemsList = [];
      let filesFolderItemsList = [];
      const isFolderEmpty = !allFolderItemsList?.length;

      allFolderItemsList.forEach((item) => {
        if (item.type === FOLDER_ITEM_TYPE_SECTION) {
          sectionFolderItemsList.push(item);
        } else {
          filesFolderItemsList.push(item);
        }
      });

      return {
        sectionFolderItemsList,
        filesFolderItemsList,
        isFolderEmpty
      };
    }, [allFolderItemsList]);

  const openDiscountCreationModal = () =>
    setShowDiscountCreationModal(true);

  const closeDiscountCreationModal = () =>
    setShowDiscountCreationModal(false);

  const openDiscountCreationModalFromDropdown = (folderData) => {
    setSelectedEventForDiscountCreation(folderData);
    setShowDiscountCreationModalFromDropdown(true);
  };

  const closeDiscountCreationModalFromDropdown = () => {
    setShowDiscountCreationModalFromDropdown(false);
    setSelectedEventForDiscountCreation('');
  };

  const openDeleteDiscountModal = () => {
    setShowDeleteDiscountModal(true);
  };

  const closeDeleteDiscountModal = () => {
    setSelectedDiscountForDeletion(null);
    setShowDeleteDiscountModal(false);
  };

  const onDiscountCreationSubmitFromDropdown = async (newDiscount) => {
    if (
      currentFolderState &&
      currentFolderState?.event === FOLDER_EVENT.EDIT
    ) {
      const currentFolderDiscountApplied =
        currentFolderState?.discountsApplied ?? [];
      currentFolderDiscountApplied.push(newDiscount);

      setCurrentFolderState((prev) => ({
        ...prev,
        discountsApplied: currentFolderDiscountApplied
      }));
    }
    setShowDiscountCreationModalFromDropdown(false);
  };

  const onDiscountCreationSubmit = async (newDiscount) => {
    const discountCodeList = currentFolderState?.newDiscountsToApply ?? [];
    const formattedValues = {
      ...newDiscount
    };

    const duplicateCode = discountCodeList.find(
      (discount) => formattedValues.code === discount.code
    );

    if (duplicateCode) {
      showErrorToast(t('discount-code-with-this-name-already-exists'));
      return;
    }

    const tempDiscountCodeList = [...discountCodeList];
    tempDiscountCodeList.push(formattedValues);

    onFolderFormFieldChange({ newDiscountsToApply: tempDiscountCodeList });
    closeDiscountCreationModal();
  };

  // Check if query param is in the url on page load
  useEffect(() => {
    if (tab) {
      setActiveFolderItemTabId(tab);
    }
  }, [tab]);

  const handeChangeFolderTabId = (tabId) => {
    setActiveFolderItemTabId(tabId);
    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, folderId, tab: tabId }
      },
      undefined,
      { shallow: true }
    );
  };

  const onDiscountDeleteModalConfirm = (discountId) => {
    onFolderFormFieldChange({
      discountsToDisable: [
        ...(currentFolderState?.discountsToDisable ?? []),
        discountId
      ]
    });

    const discountCodeList = currentFolderState?.discountsApplied ?? [];
    const filteredDiscountCodeList = discountCodeList.filter(
      (discount) => discount._id !== discountId
    );

    onFolderFormFieldChange({
      discountsApplied: filteredDiscountCodeList
    });
    closeDeleteDiscountModal();
  };

  const onDiscountDeleteClick = (selectedDiscount) => {
    if (selectedDiscount?._id) {
      setSelectedDiscountForDeletion(selectedDiscount);
      openDeleteDiscountModal();
      return;
    }

    const discountCodeList = currentFolderState?.newDiscountsToApply ?? [];
    const filteredDiscountCodeList = discountCodeList.filter(
      (discount) => discount.code !== selectedDiscount.code
    );

    onFolderFormFieldChange({
      newDiscountsToApply: filteredDiscountCodeList
    });
    closeDeleteDiscountModal();
  };

  const folderStateChanged = useMemo(
    () =>
      JSON.stringify(selectedFolder) !==
      JSON.stringify(currentFolderState),
    [selectedFolder, currentFolderState]
  );

  const openFolderReorderModal = () => setIsFolderReorderModalOpen(true);
  const closeFolderReorderModal = useCallback(({ hasChanged }) => {
    if (hasChanged) {
      setIsFolderReorderDiscardModalOpen(true);
      return;
    }
    showToast({
      type: 'info',
      text: t('changes-discarded')
    });
    setIsFolderReorderDiscardModalOpen(false);
    setIsFolderReorderModalOpen(false);
  }, []);
  function onSearchChange(term) {
    setSearchTerm(term);
  }
  const closeDeleteModal = useCallback(() => {
    setIsFolderDeleteModalOpen(false);
    setSelectedFolderId('');
  }, []);

  const openCreateFolderSideBar = useCallback((templateId) => {
    setIsFolderModalOpen(true);
    const defaultFolderData = getDefaultCreateFolderData(templateId);
    setSelectedFolder(defaultFolderData);
  }, []);

  const routeToCreateSession = useCallback((query) => {
    router.push({ pathname: CM_PORTAL_ADD_NEW_SESSION, query: query });
  }, []);

  const closeFolderModal = useCallback(() => {
    setIsFolderModalOpen(false);
  }, []);
  const openDeleteModal = useCallback(({ id }) => {
    setSelectedFolderId(id);
    setIsFolderDeleteModalOpen(true);
  }, []);

  // Open confirmation modal for deleting folder item.
  const openDeleteItemModal = useCallback((folderItem) => {
    setSelectedFolderItem(folderItem);
    setIsFolderItemDeleteModalOpen(true);
  }, []);

  const closeDeleteItemModal = useCallback(() => {
    setSelectedFolderItem(null);
    setIsFolderItemDeleteModalOpen(false);
  }, []);

  const onFolderModalCloseConfirm = useCallback(() => {
    setIsFolderConfirmationModalOpen(null);
    setIsClassCreationModalOpen(false);
    setFolderFormErrors({});
  }, []);
  const openShareModal = useCallback((shortUrl) => {
    trackGAEventWithCommunityAndUser('library_cmp_folder_share');
    setShortUrl(shortUrl);
    setShowShareModal(true);
  }, []);
  const onFolderFormFieldChange = (fields) => {
    setCurrentFolderState((prev) => ({
      ...prev,
      ...fields
    }));
    setFolderFormErrors({});
  };
  useEffect(() => {
    if (activeCommunity)
      setCurrency(activeCommunity?.baseCurrency || 'USD');
  }, [activeCommunity]);

  // this function is used to update or create the folder
  const onFolderModalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isFolderExecutionLoading) return;
      const { priceType, suggestedAmount, amount, minAmount } =
        currentFolderState || {};

      const folderEvent = currentFolderState?.event;

      if (folderEvent === FOLDER_EVENT.CREATE) {
        const params = getFolderParams({
          data: currentFolderState,
          currency,
          activeCommunity
        });
        const validationShema = getFolderValidationSchema({
          folderEvent
        });

        const isValid = await validate({
          schema: validationShema,
          formData: params,
          setErrors: setFolderFormErrors
        });

        if (!isValid) return;
        const priceInvalidError = validatePriceInputs({
          priceType,
          amount,
          minAmount,
          suggestedAmount,
          currency
        });

        if (priceInvalidError) {
          showErrorToast(priceInvalidError);
          return;
        }

        const { error, data } = await createFolder({ params });
        if (error) throw new Error(error);

        setSelectedFolder({ data });
        setIsFolderModalOpen(false);
        showConfetti();
        if (data?.amount > 0 || data?.priceConfigs?.suggestedAmount > 0) {
          verifyNasioFeePreferenceIsSet();
        }
        trackGAEvent('library_add_folder_details_success', {
          email: user?.email,
          userId: user?._id,
          communityId: activeCommunity?._id,
          communityName: activeCommunity?.title,
          communityCode: activeCommunity?.code,
          folderId: data?._id,
          productId: data?._id,
          productType: 'folder'
        });
        trackFormSubmission({
          form: {
            type: `Add Folder Form`,
            event: `Successfully Added Folder`
          },
          community: {
            name: activeCommunity?.title,
            code: activeCommunity?.code,
            id: activeCommunity?._id
          }
        });
        // moving the user to the newly created folder
        router.push(getLibraryPageUrl({ folderId: data?._id }));
      } else if (folderEvent === FOLDER_EVENT.EDIT) {
        const params = getFolderParams({
          data: currentFolderState,
          currency,
          activeCommunity
        });
        const isValid = await validate({
          schema: getFolderValidationSchema({ folderEvent }),
          formData: params,
          setErrors: setFolderFormErrors
        });

        if (!isValid) return;
        const priceInvalidError = validatePriceInputs({
          priceType,
          amount,
          minAmount,
          suggestedAmount,
          currency
        });

        if (priceInvalidError) {
          showErrorToast(priceInvalidError);
          return;
        }

        const { error, data } = await editFolder({
          params: params,
          folderId: selectedFolder._id
        });

        if (error) throw new Error(error);

        setSelectedFolder(data);
        setIsFolderModalOpen(false);

        showConfetti();
        trackFormSubmission({
          form: {
            type: `Edit Folder Form`,
            event: `Successfully Edited Folder`
          },
          community: {
            name: activeCommunity?.title,
            code: activeCommunity?.code,
            id: activeCommunity?._id
          }
        });
        trackGAEvent('library_edit_folder_details', {
          email: user?.email,
          userId: user?._id,
          communityId: activeCommunity?._id,
          communityCode: activeCommunity?.code,
          folderId: selectedFolder._id,
          productId: data?._id,
          productType: 'folder'
        });
      } else return;
    } catch (e) {
      showErrorToast(e.message);
    }
  };
  const onFolderCardClick = ({ folder }) => {
    // If its a session
    if (folder?.type === PRODUCT_TYPE.SESSION) {
      trackGAEvent(SESSION_CLICKED, {
        email: user?.email,
        userId: user?._id,
        communityId: activeCommunity?._id,
        communityCode: activeCommunity?.code,
        sessionId: folder._id,
        productId: folder?._id,
        productType: 'session'
      });
      const sessionManagementLink = get1on1SessionManagementUrl({
        id: folder?._id
      });
      router.push(sessionManagementLink);
      return;
    }
    // If its a digital product
    setSelectedFolder(folder);
    router.push(getLibraryPageUrl({ folderId: folder._id }));
  };

  const onFolderDelete = useCallback(
    async ({ id }) => {
      try {
        const folderId = id ?? selectedFolderId;
        const { error } = await deleteFolder({ folderId });
        if (error) throw new Error(error);
        closeDeleteModal();
        onFolderModalCloseConfirm();

        trackGAEvent('library_delete_folder', {
          email: user?.email,
          userId: user?._id,
          folderId: folderId,
          communityId: activeCommunity?._id,
          communityName: activeCommunity?.title,
          communityCode: activeCommunity?.code,
          productId: folderId,
          productType: 'folder'
        });

        router.push(getLibraryPageUrl());
      } catch (e) {
        showToast({ text: e.message, type: 'error' });
      }
    },
    [
      activeCommunity?._id,
      activeCommunity?.code,
      activeCommunity?.title,
      closeDeleteModal,
      deleteFolder,
      onFolderModalCloseConfirm,
      selectedFolderId,
      user?._id,
      user?.email
    ]
  );

  const removeAllFolderItemsFromFocus = useCallback(
    () => setFolderItemIdsInFocus([]),
    []
  );

  const getCurrentUploadProgress = useCallback(
    (
      currentProgress,
      fileSize,
      clientId,
      indexOfRecord,
      progress,
      total
    ) => {
      const newObject = {
        ...currentProgress,
        [clientId]: {
          ...currentProgress[clientId],
          fileSize,
          [indexOfRecord]: { progress, total }
        }
      };
      const totalProgress = Object.keys(newObject[clientId]).reduce(
        (acc, key) => {
          // This is the key we are trying to populate
          if (key === 'totalProgress' || key === 'fileSize') return acc;
          const { progress: chunkProgress, total: chunkSize } =
            newObject[clientId][key];
          const weight = chunkSize / fileSize;
          return acc + chunkProgress * weight;
        },
        0
      );
      newObject[clientId].totalProgress = Math.floor(totalProgress);
      return newObject;
    },
    []
  );

  const updateProgress = useCallback(
    ({ indexOfRecord, progressEvent, clientId, fileSize }) => {
      const { loaded, total } = progressEvent;
      const progress = Math.floor((loaded / total) * 100);
      setUploadProgressMap((prev) =>
        getCurrentUploadProgress(
          prev,
          fileSize,
          clientId,
          indexOfRecord,
          progress,
          total
        )
      );
    },
    [getCurrentUploadProgress]
  );

  const uploadFileItemsToS3SignedURL = useCallback(
    async (records, file, chunkSize) => {
      let promisesForFileUploads = [];
      for (const singleRecord of records) {
        const { partNumber, url } = singleRecord;
        // find the part of the file that needs to be uploaded
        const indexOfRecord = partNumber - 1;
        const startIndexOfChunk = indexOfRecord * chunkSize;
        const endIndexOfChunk =
          Math.min(partNumber, file.size) * chunkSize;
        // create a blob of the file part
        const part =
          file.size < endIndexOfChunk
            ? file.slice(startIndexOfChunk)
            : file.slice(startIndexOfChunk, endIndexOfChunk);

        // upload the part
        const uploadChunkFile = await uploadFileChunksToS3({
          signedUrl: url,
          blob: part,
          payload: {
            maxContentLength: 4000000000,
            maxBodyLength: 40000000000
          },
          indexOfRecord: partNumber,
          logEvent: (data) => {
            logEvent({
              communityId: activeCommunityId,
              message: `${data} ${file.name}`
            });
          },
          onProgressUpdate: (clientId, indexOfRecord, progressEvent) =>
            updateProgress({
              indexOfRecord,
              progressEvent,
              clientId,
              fileSize: file.size
            })
        });
        promisesForFileUploads.push(uploadChunkFile);
      }
      const chunkDataResponse = await Promise.allSettled(
        promisesForFileUploads
      );
      if (chunkDataResponse.some((item) => item.error)) return false;

      const parts = chunkDataResponse.map((item, index) => {
        return {
          ETag: item?.value?.data?.headers?.etag,
          PartNumber: records[index].partNumber
        };
      });
      return parts;
    },
    [activeCommunityId, logEvent, updateProgress]
  );

  const uploadFileItemChunks = useCallback(
    async ({ clientId, file, records, signedUrlId, chunkSize }) => {
      let parts;
      const fileKeys = Object.keys(records);
      let promisesForFileUploads = [];
      let indicesThatAreFailing = [];
      for (const indexOfFileStr of fileKeys) {
        const indexOfFile = parseInt(indexOfFileStr);
        const startIndex = indexOfFile * chunkSize; // 1
        const endIndex = (indexOfFile + 1) * chunkSize; // 2
        const singleBlob =
          indexOfFile < fileKeys.length
            ? file.slice(startIndex, endIndex)
            : file.slice(startIndex);
        const uploadedChunk = uploadFileChunksToS3({
          clientId,
          signedUrl: records[indexOfFile],
          blob: singleBlob,
          payload: {
            maxContentLength: 4000000000,
            maxBodyLength: 40000000000
          },
          logEvent: (data) => {
            logEvent({
              communityId: activeCommunity?._id,
              message: `File upload chunk ${indexOfFile} of ${
                fileKeys.length
              } for ${
                file.name
              }, clientId = ${clientId}, signedUrlId = ${signedUrlId} and the result is =  ${JSON.stringify(
                data
              )}`
            });
          },
          indexOfRecord: indexOfFile,
          onProgressUpdate: (clientId, indexOfRecord, progressEvent) =>
            updateProgress({
              indexOfRecord,
              progressEvent,
              clientId,
              fileSize: file.size
            })
        });

        promisesForFileUploads.push(uploadedChunk);
      }
      const chunkDataResponse = await Promise.allSettled(
        promisesForFileUploads
      );

      logEvent({
        communityId: activeCommunity?._id,
        message: `File upload chunk data response for  ${
          file.name
        }, clientId = ${clientId}, signedUrlId = ${signedUrlId} and the result is =  ${JSON.stringify(
          chunkDataResponse
        )}`
      });

      chunkDataResponse.forEach((response) => {
        const chunkDataValue = response.value;
        if (chunkDataValue?.error) {
          indicesThatAreFailing.push(chunkDataValue?.errorIndex);
        }
      });

      const chunkDataMap = chunkDataResponse.reduce((acc, item, index) => {
        const chunkDataValue = item.value;
        if (chunkDataValue?.error) {
          acc[chunkDataValue?.errorIndex] = null;
        } else {
          const response = {
            ETag: chunkDataValue?.data?.headers?.etag,
            PartNumber: index + 1
          };
          acc[index] = response;
        }
        return acc;
      }, {});

      const keysFailing = Object.keys(chunkDataMap).filter(
        (key) => chunkDataMap[key] === null
      );
      if (keysFailing.length > 0) {
        logEvent({
          communityId: activeCommunity?._id,
          message: `keys failing for file upload for ${
            file.name
          }, clientId = ${clientId}, signedUrlId = ${signedUrlId} and the folderId= ${
            selectedFolder._id
          } communityId= ${activeCommunityId} and keys failing are = ${JSON.stringify(
            keysFailing
          )}`
        });
        const signedURLPromiseList = keysFailing.map(async (key) => {
          const signedURLInfo = await getSignedURLForASingleFilePart({
            communityId: activeCommunityId,
            folderId: selectedFolder._id,
            signedUrlId: signedUrlId,
            partNumber: Number(key) + 1
          });
          const signedURLInfoForUploading = {
            url: signedURLInfo.data,
            partNumber: Number(key) + 1
          };
          return signedURLInfoForUploading;
        });
        // Get all the records
        const failedRecords = await Promise.all(signedURLPromiseList);

        const failedParts = await uploadFileItemsToS3SignedURL(
          failedRecords,
          file,
          chunkSize
        );

        failedParts.forEach((part) => {
          chunkDataMap[part.PartNumber - 1] = part;
        });

        return Object.values(chunkDataMap).sort(
          (a, b) => a.PartNumber - b.PartNumber
        );
      } else {
        // if data has an error then return false
        if (chunkDataResponse.some((item) => item.value.error))
          return false;
        parts = chunkDataResponse.map(({ value }, index) => {
          return {
            ETag: value?.data?.headers?.etag,
            PartNumber: index + 1
          };
        });
        return parts;
      }
    },
    [
      activeCommunity?._id,
      activeCommunityId,
      logEvent,
      selectedFolder?._id,
      updateProgress,
      uploadFileItemsToS3SignedURL
    ]
  );

  const saveFolderItemsList = useCallback(
    async ({
      newlyAddedFolderItems,
      allFolderItemsList,
      addingSection = false
    }) => {
      let currentItemListMap = {};
      let folderItemsUploaded = [];

      const folderItemUploadPromises = newlyAddedFolderItems.map(
        async (item) => {
          const isDataOnClient = !item?._id;
          let isMulti;
          let signedUrlInformation;
          let createFolderItemRes;

          try {
            if (item?.file) {
              // Check if uploaded file is a supported file type.
              const isFileTypeSupported = isFileSupported(item?.type);

              // If not supported, add to error list and throw error.
              if (!isFileTypeSupported) {
                setFilesErrorList((prev) => [...prev, item]);
                throw new Error(t('file-type-not-supported'));
              }

              // If filesize is greater than min chunk size, then need to do a multi part upload.
              isMulti = item.file.size > MIN_CHUNK_SIZE;
              const totalNumberOfChunks =
                await calculateTotalNumberOfChunks(item.file);

              logEvent({
                communityId: activeCommunity?._id,
                message: `Uploading file ${item.file.name} to folder ${selectedFolder?._id} at ${activeCommunity?.title} with total number of chunks ${totalNumberOfChunks}`
              });
              let signedPayload = getPayloadForSignedURL({
                item,
                isMulti,
                totalNumberOfChunks
              });
              logEvent({
                communityId: activeCommunity?._id,
                message: `Getting signed url for file ${item?.file?.name} to folder ${selectedFolder?._id} at ${activeCommunity?.title} where isMulti = ${isMulti}`
              });

              // Fetch SignedURL for uploading file.
              const { data: signedUrlInfo, error } =
                await fetchSignedUrlForFolderItem({
                  communityId: activeCommunityId,
                  folderId: selectedFolder?._id,
                  payload: signedPayload
                });

              // If error, add to error list and throw error.
              if (error) {
                logEvent({
                  communityId: activeCommunity?._id,
                  message: `Error while getting signed url Info = ${JSON.stringify(
                    error
                  )} for file ${item?.file?.name} to folder ${
                    selectedFolder?._id
                  } at ${activeCommunity?.name}`
                });
                setFilesErrorList((prev) => [...prev, item]);
                throw new Error(error);
              }
              logEvent({
                communityId: activeCommunity?._id,
                message: `Got signed url Info = ${JSON.stringify(
                  signedUrlInfo
                )} for file ${item?.file?.name} to folder ${
                  selectedFolder?._id
                } at ${activeCommunity?.name}`
              });
              signedUrlInformation = signedUrlInfo;

              let mergeSignedUrlPayload = {};

              if (isMulti) {
                const partsInformation = await uploadFileItemChunks({
                  clientId: item.clientId,
                  file: item.file,
                  records: signedUrlInfo.records,
                  signedUrlId: signedUrlInfo.signedUrlId,
                  chunkSize: Math.ceil(
                    item.file.size / totalNumberOfChunks
                  )
                });

                logEvent({
                  communityId: activeCommunity?._id,
                  message: `Got partsInformation = ${JSON.stringify(
                    partsInformation
                  )} for file ${item?.file?.name} to folder ${
                    selectedFolder?._id
                  } at ${activeCommunity?.name}`
                });
                if (!partsInformation) {
                  setFilesErrorList((prev) => [...prev, item]);
                  throw new Error(t('error-while-uploading-file'));
                }

                mergeSignedUrlPayload = {
                  signedChunkDetails: partsInformation,
                  signedUrlId: signedUrlInfo.signedUrlId
                };

                logEvent({
                  communityId: activeCommunity?._id,
                  message: `Merging signed url for file ${
                    item?.file?.name
                  } to folder ${selectedFolder?._id} at ${
                    activeCommunity?._id
                  } with mergedSignedUrlPayload = ${JSON.stringify(
                    mergeSignedUrlPayload
                  )}`
                });

                const { data } = await mergeSignedUrlForFolderItemToS3({
                  communityId: activeCommunityId,
                  folderId: selectedFolder?._id,
                  payload: mergeSignedUrlPayload
                });

                logEvent({
                  communityId: activeCommunity?._id,
                  message: `Merged signed url for file ${
                    item?.file?.name
                  } to folder ${selectedFolder?._id} at ${
                    activeCommunity?.name
                  } with data = ${JSON.stringify(data)}`
                });
              } else {
                // If single chunk upload, then upload the file to S3.
                logEvent({
                  communityId: activeCommunity?._id,
                  message: `Uploading file ${item?.file?.name} to folder ${
                    selectedFolder?._id
                  } at ${
                    activeCommunity?.name
                  } with signedUrlInfo = ${JSON.stringify(
                    signedUrlInfo
                  )} for isMulti = false `
                });
                await uploadFileChunksToS3({
                  clientId: item.clientId,
                  signedUrl: signedUrlInfo.url,
                  blob: item.file,
                  payload: {
                    maxContentLength: 4000000000,
                    maxBodyLength: 40000000000
                  },
                  logEvent: (data) => {
                    logEvent({
                      communityId: activeCommunity?._id,
                      message: `Uploading file ${
                        item?.file?.name
                      } to folder ${selectedFolder?._id} at ${
                        activeCommunity?.name
                      } with signedUrlInfo = ${JSON.stringify(
                        signedUrlInfo
                      )} for isMulti = false  amd result is ${JSON.stringify(
                        data
                      )}`
                    });
                  },
                  onProgressUpdate: (
                    clientId,
                    indexOfRecord,
                    progressEvent
                  ) =>
                    updateProgress({
                      clientId,
                      indexOfRecord: 0,
                      progressEvent,
                      fileSize: item.file.size
                    })
                });

                logEvent({
                  communityId: activeCommunity?._id,
                  message: `Uploaded file ${item?.file?.name} to folder ${
                    selectedFolder?._id
                  } at ${
                    activeCommunity?.name
                  } with signedUrlInfo = ${JSON.stringify(
                    signedUrlInfo
                  )} for isMulti = false `
                });
              }
            }

            let payloadInfoForFolderItem = { ...item };

            if (isDataOnClient) {
              delete payloadInfoForFolderItem._id;
              delete payloadInfoForFolderItem.file;

              // if upload is an asset, add signedUrlId to payload.
              if (
                [
                  FILE_TYPE.FILE,
                  FILE_TYPE.VIDEO,
                  FILE_TYPE.IMG,
                  FILE_TYPE.AUDIO
                ].includes(item.type)
              ) {
                payloadInfoForFolderItem.signedUrlId =
                  signedUrlInformation?.signedUrlId;
              }

              // If folder is unpublished, then set folder item status to published.
              if (selectedFolder?.status === STATUS_UNPUBLISHED) {
                payloadInfoForFolderItem.status = STATUS_PUBLISHED;
              }

              // create folder item.
              createFolderItemRes = await createCommunityFolderItem({
                communityId: activeCommunityId,
                folderId: selectedFolder?._id,
                payload: payloadInfoForFolderItem
              });

              const { data, error } = createFolderItemRes;

              logEvent({
                communityId: activeCommunity?._id,
                message: `Created folder item ${JSON.stringify(
                  data
                )} for file ${item?.file?.name} to folder ${
                  selectedFolder?._id
                } at ${activeCommunity?._id}`
              });

              if (error) throw new Error(error);
              if (data) {
                folderItemsUploaded.push(data);
              }
            }

            setFilesUploadingStatus((prev) => ({
              ...prev,
              [item?.clientId]: FILE_STATUS.SUCCESS
            }));

            return createFolderItemRes; // { data, error}
          } catch (e) {
            showToast({ type: 'error', text: e.message });

            setFilesUploadingStatus((prev) => ({
              ...prev,
              [item?.clientId]: FILE_STATUS.ERROR
            }));

            // remove the file from the list
            const currentItemListMap = [];
            allFolderItemsList.forEach((folderItem) => {
              if (folderItem?.clientId !== item?.clientId) {
                currentItemListMap.push(folderItem);
              }
            });
            setFolderItemsList(currentItemListMap);

            return { error };
          }
        }
      );

      let uploadedFolderItemsData;
      try {
        uploadedFolderItemsData = await Promise.all(
          folderItemUploadPromises
        );
      } catch (error) {
        console.error(error);
      }

      setTotalFileSize(
        newlyAddedFolderItems.reduce(
          (acc, item) => acc + item?.file?.size ?? 0,
          0
        )
      );

      // Reorder needs to only be called if adding a section.
      // This is because newly added section gets added after all existing sections and before existing root files.
      // For files addition in section or in root, we add them to the end, so reordering is not required.
      if (addingSection) {
        await updateCommunityFolderItemsOrder({
          communityId: activeCommunity?._id,
          folderId: selectedFolder?._id,
          payload: {
            folderItems: allFolderItemsList
          }
        });
      }

      const { data, error } = await getCommunityFolderItems({
        communityId: activeCommunityId,
        folderId: selectedFolder?._id
      });

      if (error) {
        showErrorToast(error);
        return { error };
      }

      const { folderItems } = data;

      const allFolderItemsWithIndices =
        addIndexAndClientIdToFolderItemsRecursively(folderItems);

      setFolderItemsList(allFolderItemsWithIndices);

      setUploadProgressMap({});
      setStatus(STATUS.UPLOADED);
      setTimeStarted(0);
      setTotalFileSize(0);
      setUploadInProgress(false);
      return {
        error: Object.keys(filesUploadingStatus).some(
          (statusKey) =>
            filesUploadingStatus[statusKey] === FILE_STATUS.ERROR
        ),
        items: currentItemListMap,
        uploadedFolderItemsData
      };
    },
    [
      activeCommunity?._id,
      activeCommunity?.name,
      activeCommunityId,
      filesUploadingStatus,
      logEvent,
      selectedFolder?._id,
      selectedFolder?.status,
      updateProgress,
      uploadFileItemChunks
    ]
  );

  const saveAndUploadTheFile = useCallback(
    async ({
      newlyAddedFolderItems,
      allFolderItemsList,
      addingSection = false
    }) => {
      // Set status to published
      const newlyAddedFolderItemsFormatted = getFormattedFileParams({
        list: newlyAddedFolderItems
      });

      const { error, uploadedFolderItemsData } = await saveFolderItemsList(
        {
          newlyAddedFolderItems: newlyAddedFolderItemsFormatted,
          allFolderItemsList,
          addingSection
        }
      );

      if (error) {
        trackGAEventWithCommunityAndUser('library_folder_upload_failure', {
          folderId: selectedFolder?._id
        });
        showToast({
          text: t('error-while-uploading-the-file'),
          type: 'error',
          nplVersion: 2
        });
        return;
      }

      // Set state
      if (addingSection) {
        // setting state and toast is mixed for adding section. Since state needs to be set from inside the toast
        showToast({
          text: t('section-created'),
          type: 'default',
          nplVersion: 2,
          customInnerUi: () => (
            <div className="flex w-full items-center justify-between ">
              <p className="text-body-md font-medium">
                {t('section-created')}
              </p>
              <NPLButton
                size="sm"
                hierarchy="outline"
                onClick={() => {
                  const sectionFolderItemId =
                    uploadedFolderItemsData?.[0]?.data?._id;
                  if (sectionFolderItemId) {
                    setFolderItemIdsInFocus((prev) => [
                      ...prev,
                      sectionFolderItemId
                    ]);
                  }
                }}
                buttonText={t('view')}
              />
            </div>
          ),

          showRemoveToastBtn: false
        });
      }

      if (!addingSection) {
        // set focus on folder items.
        const focusFolderItemId = uploadedFolderItemsData?.[0]?.data?._id;
        focusFolderItemId && setFolderItemIdsInFocus([focusFolderItemId]);
      }

      // Show Toast
      if (!addingSection) {
        showToast({
          text:
            selectedFolder?.status === STATUS_PUBLISHED
              ? t('your-content-has-been-updated')
              : t('awesome-your-content-was-uploaded'),
          type: 'success',
          nplVersion: 2
        });
      }

      // TraCK GA EVENT
      if (selectedFolder?.status != STATUS_PUBLISHED) {
        const type = selectedFolder.type;
        if (type === PRODUCT_TYPE.DIGITAL_PRODUCT) {
          trackGAEvent('library_save_upload_course_success', {
            email: user?.email,
            userId: user?.id,
            communityId: activeCommunity?._id,
            communityName: activeCommunity?.title,
            communityCode: activeCommunity?.code
          });
        } else {
          trackGAEvent('library_save_upload_collection_success', {
            email: user?.email,
            userId: user?.id,
            communityId: activeCommunity?._id,
            communityName: activeCommunity?.title,
            communityCode: activeCommunity?.code
          });
        }
      }
    },
    [
      activeCommunity?._id,
      activeCommunity?.code,
      activeCommunity?.title,
      saveFolderItemsList,
      selectedFolder?._id,
      selectedFolder?.status,
      selectedFolder?.type,
      user?.email,
      user?.id
    ]
  );

  // this will run when we add the content from the front end
  const onAddItemsToFolderList = useCallback(
    async (listParam = {}) => {
      // list is array of new files that were added.
      // parentSectionId is the id of the section in which the files were added. Null if added in root
      const {
        list,
        addingSection = false,
        parentSectionId = null
      } = listParam;

      // clean up the focus from all items.
      removeAllFolderItemsFromFocus();

      // Get uploaded files data in desired format.
      const newlyUploadedFilesList = await getFolderItemDataFormatted({
        list,
        parentSectionId
      });

      // Check if any files exist after formatting
      if (!newlyUploadedFilesList?.length) return;

      // Calcualte the size of all files to be uploaded.
      const sizeOfNewUploadFiles = newlyUploadedFilesList.reduce(
        (acc, item) => acc + (item?.file?.size ?? 0),
        0
      );

      // Check here if size of the upload is within allotted limits.
      if (sizeOfNewUploadFiles > 0) {
        const { error: uploadLimitError } =
          await checkCommunityStorageService({
            communityId: activeCommunityId,
            uploadingFileSize: sizeOfNewUploadFiles
          });

        if (uploadLimitError) {
          showToast({
            text: uploadLimitError,
            type: 'error',
            nplVersion: 2,
            CTAButtonProps: {
              onClick: () => {},
              text: t('got-it')
            },
            dismissOnClick: true
          });
          return;
        }
      }

      // Initialize
      let updatedFolderItemsList = [];
      let newlyAddedFolderItemsWithClientId = [];

      // If files are being added to a section
      if (parentSectionId) {
        updatedFolderItemsList = [...allFolderItemsList];

        // Find the section folder item in which files are being added.
        const sectionIndex = updatedFolderItemsList?.findIndex(
          (item) =>
            item.type === FOLDER_ITEM_TYPE_SECTION &&
            item._id === parentSectionId
        );

        if (sectionIndex === -1) {
          // This should not happen. adding as a failsafe.
          return;
        }

        // Get the items in the section.
        let sectionItems = [
          ...(updatedFolderItemsList?.[sectionIndex]?.items || [])
        ];

        // // Prepend new files to the section items.
        // sectionItems = [...newlyUploadedFilesList, ...sectionItems];
        // Append new files to the section items.
        sectionItems = [...sectionItems, ...newlyUploadedFilesList];

        // Serialize index and add clientId to new files.
        sectionItems = addIndexAndClientIdToFolderItems(sectionItems);

        // Save the updates in updatedFolderItemsList
        updatedFolderItemsList[sectionIndex].items = sectionItems;

        // clientId is used for referencing files later. Hence putting it in a separate variable to reuse.
        // newlyAddedFolderItemsWithClientId = sectionItems.slice(
        //   0,
        //   newlyUploadedFilesList.length
        // );
        // clientId is used for referencing files later. Hence putting it in a separate variable to reuse.
        newlyAddedFolderItemsWithClientId = sectionItems.slice(
          newlyUploadedFilesList.length * -1
        ); // if 3 files are added, you want to do slice(-3) to get the last 3 items.
      } else if (addingSection) {
        // if a new section is being added to the folder.
        // In this case we want to add the item after all existing sections but before all files.
        updatedFolderItemsList = [
          ...sectionFolderItemsList,
          ...newlyUploadedFilesList,
          ...filesFolderItemsList
        ];

        // Add clientId to new files and searialize index.
        updatedFolderItemsList = addIndexAndClientIdToFolderItems(
          updatedFolderItemsList
        );

        const indexOfAddedSection = sectionFolderItemsList.length;
        // clientId is used for referencing files later. Hence putting it in a separate variable to reuse.
        newlyAddedFolderItemsWithClientId = [
          updatedFolderItemsList[indexOfAddedSection]
        ];
      } else {
        // If files are added to root and NOT in any particular section.
        // // Prepend new file uploads to folder items in state.
        // updatedFolderItemsList = [
        //   ...newlyUploadedFilesList,
        //   ...allFolderItemsList
        // ];
        // Append new file uploads to folder items in state.
        updatedFolderItemsList = [
          ...allFolderItemsList,
          ...newlyUploadedFilesList
        ];

        // Add clientId to new files and searialize index.
        updatedFolderItemsList = addIndexAndClientIdToFolderItems(
          updatedFolderItemsList
        );

        // // clientId is used for referencing files later. Hence putting it in a separate variable to reuse.
        // newlyAddedFolderItemsWithClientId = updatedFolderItemsList.slice(
        //   0,
        //   newlyUploadedFilesList.length
        // );
        // clientId is used for referencing files later. Hence putting it in a separate variable to reuse.
        newlyAddedFolderItemsWithClientId = updatedFolderItemsList?.slice(
          newlyUploadedFilesList.length * -1
        ); // if 3 files are added, you want to do slice(-3) to get the last 3 items.
      }
      setFolderItemsList(updatedFolderItemsList);
      setStatus(STATUS.UPLOADING);

      // Create loading
      const loadingMap = newlyAddedFolderItemsWithClientId.reduce(
        (acc, item) => {
          return { ...acc, [item.clientId]: FILE_STATUS.LOADING };
        },
        {}
      );
      setFilesUploadingStatus(loadingMap);

      // Set start time
      setTimeStarted(new Date().getTime());

      // Set size of the upload for upload time calculation
      setTotalFileSize(sizeOfNewUploadFiles);

      // Upload the files.
      saveAndUploadTheFile({
        newlyAddedFolderItems: newlyAddedFolderItemsWithClientId,
        allFolderItemsList: updatedFolderItemsList,
        addingSection
      });
    },
    [
      activeCommunityId,
      allFolderItemsList,
      filesFolderItemsList,
      removeAllFolderItemsFromFocus,
      saveAndUploadTheFile,
      sectionFolderItemsList
    ]
  );

  const onFolderUnpublish = useCallback(
    async ({ id }) => {
      try {
        const { data, error } = await updatePublishFolder({
          folderId: id,
          param: { status: 'Unpublished' }
        });

        if (error) throw new Error(error);

        setSelectedFolder(data);
        if (data.type === PRODUCT_TYPE.DIGITAL_PRODUCT) {
          trackGAEvent('library_course_unpublish', {
            email: user?.email,
            userId: user?._id,
            communityId: activeCommunity?._id,
            communityName: activeCommunity?.title,
            communityCode: activeCommunity?.code,
            courseId: id,
            productId: id,
            productType: 'folder'
          });
        } else {
          trackGAEvent('library_collection_unpublish', {
            email: user?.email,
            userId: user?._id,
            communityId: activeCommunity?._id,
            communityName: activeCommunity?.title,
            communityCode: activeCommunity?.code,
            collectionId: id,
            productId: id,
            productType: 'folder'
          });
        }
        showToast({
          text: t('unpublished-successfully'),
          type: 'success',
          nplVersion: 2
        });
        onFolderModalCloseConfirm();
      } catch (e) {
        showToast({ text: e.message, type: 'error' });
      }
    },
    [
      activeCommunity?._id,
      activeCommunity?.code,
      activeCommunity?.title,
      onFolderModalCloseConfirm,
      updatePublishFolder,
      user?._id,
      user?.email
    ]
  );

  const onFolderPublish = useCallback(
    async ({ id }) => {
      try {
        const { data, error } = await updatePublishFolder({
          folderId: id,
          param: { status: 'Published' }
        });

        if (error) throw new Error(error);

        clearFilesErrorList();
        setSelectedFolder(data);
        showConfetti();

        trackGAEvent('library_folder_publish', {
          email: user?.email,
          userId: user?._id,
          communityId: activeCommunity?._id,
          communityName: activeCommunity?.title,
          communityCode: activeCommunity?.code,
          folderId: id,
          productId: id,
          productType: 'folder'
        });
        showSuccessToast(
          t('congrats-your-content-has-been-published-and-is-now-live')
        );

        openShareModal(
          getDigitalProductAccessPageLink({
            communitySlug: activeCommunity?.link,
            productSlug: data?.resourceSlug,
            fullUrl: true,
            hasAccess: false
          })
        );
        showConfetti();

        // Refresh task progress percentage.
        refreshTaskProgressPerc();
      } catch (e) {
        showToast({ text: e.message, type: 'error' });
      }
    },
    [
      activeCommunity?._id,
      activeCommunity?.code,
      activeCommunity?.title,
      refreshTaskProgressPerc,
      updatePublishFolder,
      user?._id,
      user?.email
    ]
  );

  const onFolderItemEditClick = useCallback(({ item }) => {
    setSelectedFolderItem(item);
    setIsFolderItemModalOpen(true);
  }, []);

  const onFolderEdit = useCallback(
    ({ selectedFolder }) => {
      setSelectedFolder({
        ...selectedFolder,
        event: FOLDER_EVENT.EDIT,
        amount: selectedFolder?.amount
      });

      trackGAEvent('library_edit_folder_content', {
        email: user?.email,
        userId: user?._id,
        communityId: activeCommunity?._id,
        communityName: activeCommunity?.title,
        communityCode: activeCommunity?.code,
        folderId: selectedFolder?._id
      });
      setIsFolderModalOpen(true);
    },
    [
      activeCommunity?._id,
      activeCommunity?.code,
      activeCommunity?.title,
      user?._id,
      user?.email
    ]
  );

  const createSectionFolderItem = useCallback(
    async (params) => {
      // const { title, description } = params;
      await onAddItemsToFolderList({
        list: [params],
        addingSection: true
      });
    },
    [onAddItemsToFolderList]
  );

  const onFolderItemsDrop = useCallback(
    ({ files, parentSectionId }) => {
      if (files.length > 10) {
        showToast({
          text: t('you-can-only-upload-10-files-at-a-time'),
          type: 'error'
        });

        logEvent({
          communityId: activeCommunity?._id,
          message: getFormattedLogTextForMaxFilesUpload(
            activeCommunityId,
            10,
            selectedFolderId,
            user._id
          )
        });
        return;
      }

      const fileDetails = files.reduce(
        (acc, file) => {
          const isSupported = acceptedTypes.includes(file.type);
          if (!isSupported) {
            acc.errorMap.push(
              `${file.name} (${file.type}) file type not supported`
            );
            return acc;
          }
          let sendType = '';
          if (videoTypes.includes(file.type)) {
            sendType = FILE_TYPE.VIDEO;
            trackGAEvent(LIB_COURSE_ADD_VIDEO_EVENT, {
              email: user?.email,
              user_id: user?.email,
              communityId: activeCommunity?._id,
              communityName: activeCommunity?.title,
              communityCode: activeCommunity?.code,
              courseId: selectedFolder._id
            });
          } else if (
            file.type === 'audio/mpeg' ||
            file.type === 'audio/wav'
          ) {
            sendType = FILE_TYPE.AUDIO;
          } else if (imageTypes.includes(file.type)) {
            sendType = FILE_TYPE.IMG;
          } else {
            sendType = FILE_TYPE.FILE;
            trackGAEvent(LIB_COURSE_ADD_FILE_EVENT, {
              email: user?.email,
              user_id: user?.email,
              communityId: activeCommunity?._id,
              communityName: activeCommunity?.title,
              communityCode: activeCommunity?.code,
              collectionId: selectedFolder._id
            });
          }
          acc.files.push({
            file,
            type: sendType
          });

          return acc;
        },
        { files: [], errorMap: [] }
      );
      setFilesErrorList(fileDetails.errorMap);

      if (fileDetails.errorMap.length > 0) {
        fileDetails.errorMap.forEach((error) => {
          showToast({
            text: `${error}`,
            type: 'error'
          });
          logEvent({
            communityId: activeCommunity?._id,
            message: getFormattedTextForFileNotSupported(
              activeCommunityId,
              error,
              selectedFolderId,
              user._id
            )
          });
        });

        return;
      }

      logEvent({
        communityId: activeCommunity?._id,
        message: getFormattedTextForFileItemsToBeUploaded(
          activeCommunityId,
          JSON.stringify(fileDetails.files),
          selectedFolderId,
          user._id
        )
      });

      onAddItemsToFolderList({ list: fileDetails.files, parentSectionId });
    },
    [
      activeCommunity?._id,
      activeCommunity?.code,
      activeCommunity?.title,
      activeCommunityId,
      logEvent,
      onAddItemsToFolderList,
      selectedFolder?._id,
      selectedFolderId,
      user?._id,
      user?.email
    ]
  );

  const clearFilesErrorList = () => setFilesErrorList([]);

  const getFolderItemsInfo = useCallback(
    async ({ folderId, communityId, async = false }) => {
      try {
        const { data } = await getFolderDetails({
          communityId: communityId,
          folderId: folderId,
          async
        });
        if (data) {
          const { folderItems, ...folderDetails } = data;

          setSelectedFolder({
            ...folderDetails
          });

          const allFolderItemsWithIndices =
            addIndexAndClientIdToFolderItemsRecursively(folderItems);

          setFolderItemsList(allFolderItemsWithIndices);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [getFolderDetails]
  );

  /**
   * There are 6 types of reordering that can happen
   * 1. A section can be moved to a new position in the root.
   * 2. A file can be moved to a new position in the root.
   * 3. A file can be moved within a section.
   * 4. A file can be moved from root to inside a section.
   * 5. A file can be moved from inside a section to root.
   * 6. A file can be moved from inside a section to inside another section.
   * Note:-
   * On root level, sections are always above files.
   * A file cannot be moved above a section and a section cannot be moved below a file.
   */

  const onDndDragEnd = useCallback(
    async (result) => {
      // if dropped in a non droppable zone, do nothing
      if (!result.destination) return;

      const { source, destination } = result;

      // if dragged and dropped in the same position, do nothing
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      // Identify the type of reordering that is happening.
      // Check comment above function for all types of reordering.
      const isSectionReorder =
        source.droppableId === SECTION_DROPPABLE_ID &&
        source.droppableId === destination.droppableId;
      const isRootFileReorder =
        source.droppableId === ROOT_FILES_DROPPABLE_ID &&
        source.droppableId === destination.droppableId;
      const isFileMovedWithinSection =
        source.droppableId.startsWith(SECTION_FILES_DROPPABLE_ID_PREFIX) &&
        source.droppableId === destination.droppableId;
      const isFileMovedFromOneSectionToAnother =
        source.droppableId.startsWith(SECTION_FILES_DROPPABLE_ID_PREFIX) &&
        destination.droppableId.startsWith(
          SECTION_FILES_DROPPABLE_ID_PREFIX
        ) &&
        source.droppableId != destination.droppableId;
      const isFileMovedFromRootToSection =
        source.droppableId === ROOT_FILES_DROPPABLE_ID &&
        destination.droppableId.startsWith(
          SECTION_FILES_DROPPABLE_ID_PREFIX
        );
      const isFileMovedFromSectionToRoot =
        source.droppableId.startsWith(SECTION_FILES_DROPPABLE_ID_PREFIX) &&
        destination.droppableId === ROOT_FILES_DROPPABLE_ID;

      const strAllFolderItemsList = JSON.stringify(allFolderItemsList);
      const prevFolderItemsList = JSON.parse(strAllFolderItemsList);
      let reorderFolderItemsPayload = [];

      if (isSectionReorder) {
        setFolderItemsList((prevFolderItemsList) => {
          // Reorder folder items
          let reorderedFolderItemsList = reorder(
            prevFolderItemsList,
            source.index,
            destination.index
          );

          // Update index values in items to represent new position.
          reorderedFolderItemsList = addIndexAndClientIdToFolderItems(
            reorderedFolderItemsList
          );

          reorderFolderItemsPayload = reorderedFolderItemsList;

          return reorderedFolderItemsList;
        });
      }

      if (isRootFileReorder) {
        setFolderItemsList((prevFolderItemsList) => {
          // Find the index of the first file in the root.
          // File can have multiple types like video, image, link, etc. The best way to get it is the get the first item which is not a section.
          let filesStartIndex = prevFolderItemsList.findIndex(
            (folderItem) => folderItem.type != FOLDER_ITEM_TYPE_SECTION
          );

          // If non section type folder item is not found, then start idx of the file will be the last index of the folder items lis5t.
          filesStartIndex =
            filesStartIndex === -1
              ? prevFolderItemsList.length
              : filesStartIndex;

          // Adding as fail safe. Should not happen since it's reordeing of files. so not finding files would be incorrect.
          if (filesStartIndex === -1) return prevFolderItemsList;

          const sourceIdx = filesStartIndex + source.index;
          const destinationIdx = filesStartIndex + destination.index;

          // Reorder folder items
          let reorderedFolderItemsList = reorder(
            prevFolderItemsList,
            sourceIdx,
            destinationIdx
          );

          // Update index values in items to represent new position.
          reorderedFolderItemsList = addIndexAndClientIdToFolderItems(
            reorderedFolderItemsList
          );

          // updatedFolderItemsList = reorderedFolderItemsList;
          reorderFolderItemsPayload = reorderedFolderItemsList;

          return reorderedFolderItemsList;
        });
      }

      if (isFileMovedWithinSection) {
        setFolderItemsList((prevFolderItemsList) => {
          let updatedFolderItemsList = [...prevFolderItemsList];

          const sectionFolderItemId = source.droppableId.replace(
            SECTION_FILES_DROPPABLE_ID_PREFIX,
            ''
          );

          // Find the index of the section folder item in which the file was moved.
          const sectionFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === sectionFolderItemId
          );

          // Adding as fail safe. Should not happen since it's reordeing in that section.
          if (sectionFolderItemIdx === -1) return prevFolderItemsList;

          // Reorder the items inside section
          const sectionFolderItem =
            prevFolderItemsList[sectionFolderItemIdx];
          let reorderdFolderItemsInSection = reorder(
            sectionFolderItem.items,
            source.index,
            destination.index
          );
          // Update index values in items to represent new position.
          reorderdFolderItemsInSection = addIndexAndClientIdToFolderItems(
            reorderdFolderItemsInSection
          );
          const updatedSectionFolderItem = {
            ...sectionFolderItem,
            items: reorderdFolderItemsInSection
          };

          // Update folder items list.
          updatedFolderItemsList[sectionFolderItemIdx] =
            updatedSectionFolderItem;

          // set payload for reorder api
          reorderFolderItemsPayload = reorderdFolderItemsInSection;

          return updatedFolderItemsList;
        });
      }

      if (isFileMovedFromOneSectionToAnother) {
        setFolderItemsList((prevFolderItemsList) => {
          let updatedFolderItemsList = [...prevFolderItemsList];

          // Get source and destination folder item ids
          const sourceFolderItemId = source.droppableId.replace(
            SECTION_FILES_DROPPABLE_ID_PREFIX,
            ''
          );
          const destinationFolderItemId = destination.droppableId.replace(
            SECTION_FILES_DROPPABLE_ID_PREFIX,
            ''
          );

          // Get position of source and destination folder items in the folder items list
          const sourceFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === sourceFolderItemId
          );
          const destinationFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === destinationFolderItemId
          );

          // Get source and destination folder item objects
          const sourceFolderItem = {
            ...prevFolderItemsList[sourceFolderItemIdx]
          };
          const destinationFolderItem = {
            ...prevFolderItemsList[destinationFolderItemIdx]
          };

          // delete the dragged item from the source folder
          let [draggedItem] = sourceFolderItem.items.splice(
            source.index,
            1
          );

          // Update parentSectionId of the draggedId to now be the destination folder item id.
          draggedItem.parentSectionId = destinationFolderItemId;

          // add dragged item to destination folder
          destinationFolderItem.items.splice(
            destination.index,
            0,
            draggedItem
          );

          // reindex source and destination folder items
          sourceFolderItem.items = addIndexAndClientIdToFolderItems(
            sourceFolderItem.items
          );
          destinationFolderItem.items = addIndexAndClientIdToFolderItems(
            destinationFolderItem.items
          );

          updatedFolderItemsList[sourceFolderItemIdx] = sourceFolderItem;
          updatedFolderItemsList[destinationFolderItemIdx] =
            destinationFolderItem;

          // set payload for reorder api
          reorderFolderItemsPayload = [
            ...sourceFolderItem.items,
            ...destinationFolderItem.items
          ];

          return updatedFolderItemsList;
        });
      }

      if (isFileMovedFromRootToSection) {
        setFolderItemsList((prevFolderItemsList) => {
          let updatedFolderItemsList = [...prevFolderItemsList];
          // Find the index of the first file in the root.
          // File can have multiple types like video, image, link, etc. The best way to get it is the get the first item which is not a section.
          let filesStartIndex = prevFolderItemsList.findIndex(
            (folderItem) => folderItem.type != FOLDER_ITEM_TYPE_SECTION
          );

          // If non section type folder item is not found, then start idx of the file will be the last index of the folder items lis5t.
          filesStartIndex =
            filesStartIndex === -1
              ? prevFolderItemsList.length
              : filesStartIndex;
          const sourceIdx = filesStartIndex + source.index;

          // Get source and destination folder item ids
          const sectionFolderItemId = destination.droppableId.replace(
            SECTION_FILES_DROPPABLE_ID_PREFIX,
            ''
          );

          // Get position of destination folder item in the folder items list
          const sectionFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === sectionFolderItemId
          );

          // Get destination folder item object
          const sectionFolderItem = {
            ...prevFolderItemsList[sectionFolderItemIdx]
          };

          let draggedItem = prevFolderItemsList[sourceIdx];

          // Update parentSectionId of the draggedId to now be the destination folder item id.
          draggedItem.parentSectionId = sectionFolderItemId;

          // add dragged item to destination folder
          sectionFolderItem.items.splice(
            destination.index,
            0,
            draggedItem
          );

          updatedFolderItemsList[sectionFolderItemIdx] = sectionFolderItem;

          // Note: It is important we delete the item after updating the destination folder item.
          // If we delete it before, then the index of the destination folder item will change.
          // delete the dragged item from the root
          updatedFolderItemsList?.splice(sourceIdx, 1);

          // reorder indexes
          updatedFolderItemsList = addIndexAndClientIdToFolderItems(
            updatedFolderItemsList
          );
          const sectionFolderItemsReindexed =
            addIndexAndClientIdToFolderItems(sectionFolderItem.items);

          // Update section folder item in folder items list.
          // NOTE. Since we currently do not allow files being moved above sections, we can safely assume that the section index will not change after dropping the file in root.
          // Putting this logic here anyway for future proofing.
          const newSectionFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === sectionFolderItemId
          );
          updatedFolderItemsList[newSectionFolderItemIdx] = {
            ...updatedFolderItemsList[newSectionFolderItemIdx],
            items: sectionFolderItemsReindexed
          };

          // set payload for reorder api
          reorderFolderItemsPayload = [
            ...updatedFolderItemsList,
            ...sectionFolderItemsReindexed
          ];

          return updatedFolderItemsList;
        });
      }

      if (isFileMovedFromSectionToRoot) {
        setFolderItemsList((prevFolderItemsList) => {
          let updatedFolderItemsList = [...prevFolderItemsList];

          // Find the index of the first file in the root.
          // File can have multiple types like video, image, link, etc. The best way to get it is the get the first item which is not a section.
          let filesStartIndex = prevFolderItemsList.findIndex(
            (folderItem) => folderItem.type != FOLDER_ITEM_TYPE_SECTION
          );

          // If non section type folder item is not found, then start idx of the file will be the last index of the folder items lis5t.
          filesStartIndex =
            filesStartIndex === -1
              ? prevFolderItemsList.length
              : filesStartIndex;
          const destinationIdx = filesStartIndex + destination.index;

          // Get source folder item id i.e section
          const sectionFolderItemId = source.droppableId.replace(
            SECTION_FILES_DROPPABLE_ID_PREFIX,
            ''
          );

          // Get section folder item object
          const sectionFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === sectionFolderItemId
          );
          let sectionFolderItem = {
            ...prevFolderItemsList[sectionFolderItemIdx]
          };

          // delete the dragged item from the section
          let [draggedItem] = sectionFolderItem.items.splice(
            source.index,
            1
          );

          // Update parentSectionId of the draggedId to now be null.
          draggedItem.parentSectionId = null;

          // add dragged item to destination folder
          updatedFolderItemsList?.splice(destinationIdx, 0, draggedItem);

          // Reindex root items
          updatedFolderItemsList = addIndexAndClientIdToFolderItems(
            updatedFolderItemsList
          );

          // Reindex section items
          const sectionFolderItemsReindexed =
            addIndexAndClientIdToFolderItems(sectionFolderItem.items);
          sectionFolderItem.items = sectionFolderItemsReindexed;

          // Update section folder item in folder items list.
          // NOTE. Since we currently do not allow files being moved above sections, we can safely assume that the section index will not change after dropping the file in root.
          // Putting this logic here anyway for future proofing.
          const newSectionFolderItemIdx = prevFolderItemsList.findIndex(
            (folderItem) => folderItem._id === sectionFolderItemId
          );
          updatedFolderItemsList[newSectionFolderItemIdx] = {
            ...updatedFolderItemsList[newSectionFolderItemIdx],
            items: sectionFolderItemsReindexed
          };

          // set payload for reorder api
          reorderFolderItemsPayload = [
            ...updatedFolderItemsList, // reindexed root items
            ...sectionFolderItemsReindexed // reindexed section items
          ];

          return updatedFolderItemsList;
        });
      }

      // Send updated folder items list to server.
      const { error } = await updateCommunityFolderItemsOrder({
        communityId: activeCommunity?._id,
        folderId: selectedFolder?._id,
        payload: {
          folderItems: reorderFolderItemsPayload
        }
      });

      if (error) {
        // revert back to previous state
        setFolderItemsList(prevFolderItemsList);

        // show error toast
        showErrorToast(error);
      }

      getFolderItemsInfo({
        folderId: selectedFolder?._id,
        communityId: activeCommunity?._id,
        async: true
      });
    },
    [
      activeCommunity?._id,
      allFolderItemsList,
      getFolderItemsInfo,
      selectedFolder?._id
    ]
  );

  const resetDataState = useCallback(() => {
    setSelectedFolder(null);
    setFolderFormErrors({});
    setFolderItemsList([]);
    setFilesErrorList([]);
    setStatus(STATUS.IDLE);
  }, []);

  const discardAllChanges = useCallback(
    ({ force = false }) => {
      if (uploadInProgress && !force) {
        setIsConfirmBackModalOpen(true);
        return;
      }
      router.push(getLibraryPageUrl());
      setIsConfirmBackModalOpen(false);
      resetDataState();
    },
    [resetDataState, uploadInProgress]
  );

  const handlerDeleteFolderItem = useCallback(async () => {
    try {
      const folderItemId = selectedFolderItem?._id;
      if (!folderItemId) return;

      setIsLoading(true);

      await deleteCommunityFolderItem({
        communityId: activeCommunityId,
        folderItemId
      });

      showToast({
        text:
          selectedFolderItem?.type === FOLDER_ITEM_TYPE_SECTION
            ? t('section-deleted')
            : t('content-deleted-successfully'),
        type: 'info'
      });

      if (selectedFolderItem?.parentSectionId) {
        // If item was deleted from inside a section.
        setFolderItemsList((prevFolderItemsList) => {
          // find index of section in which item was deleted.
          const sectionIndex = prevFolderItemsList.findIndex(
            (item) => item._id === selectedFolderItem?.parentSectionId
          );

          // if not found, then return prev state.
          if (sectionIndex === -1) return prevFolderItemsList;

          // get section items
          const sectionItems = [
            ...(prevFolderItemsList[sectionIndex]?.items || [])
          ];

          // get all except the deleted item.
          prevFolderItemsList[sectionIndex].items = sectionItems.filter(
            (items) => {
              return items._id !== folderItemId;
            }
          );

          return prevFolderItemsList;
        });
      } else {
        // If item was deleted from root.
        setFolderItemsList((prevFolderItemsList) => {
          return prevFolderItemsList.filter(
            (item) => item._id !== folderItemId
          );
        });
      }
    } catch (e) {
      showToast({ text: `Delete failed: ${e.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
      closeDeleteItemModal();
    }
  }, [
    selectedFolderItem,
    activeCommunityId,
    closeDeleteItemModal,
    setIsLoading
  ]);

  const estimatedTimeLeft = useMemo(() => {
    if (timeStarted !== 0) {
      const sizeUploaded = (Object.keys(uploadProgressMap) ?? []).reduce(
        (total, item) => {
          const fileSize = uploadProgressMap[item]?.fileSize ?? 0;
          const totalProgress =
            uploadProgressMap[item]?.totalProgress ?? 0;
          const szUploaded = (totalProgress / 100) * fileSize;
          return total + szUploaded;
        },
        0
      );
      const timeElapsed = Date.now() - timeStarted;
      const speed = sizeUploaded / timeElapsed;
      const resultInMs = (totalFileSize - sizeUploaded) / speed;
      const minutes = Math.floor(resultInMs / 60000);
      const seconds = ((resultInMs % 60000) / 1000).toFixed(0);
      return { minutes: Math.abs(minutes), seconds: Math.abs(seconds) };
    }
    return { minutes: 'Infinity' };
  }, [timeStarted, totalFileSize, uploadProgressMap]);

  const removeFolderItemFromFocus = useCallback((folderItemId) => {
    setFolderItemIdsInFocus((prev) =>
      prev.filter((id) => id !== folderItemId)
    );
  }, []);

  const resetUploadedStatus = useCallback(
    () => setStatus(STATUS.IDLE),
    []
  );

  const goToFolderEditScreen = useCallback(
    () =>
      router.push(
        getLibraryPageUrl({
          folderId: selectedFolder?._id,
          isEditing: true
        })
      ),
    [selectedFolder?._id]
  );

  const onFolderReorder = async ({ list }) => {
    try {
      const formattedData = list.reduce(
        (acc, item) => {
          if (item?.hasChanged) {
            acc.list.push({
              communityFolderId: item._id,
              index: item.index
            });
            Object.assign(acc.map, { [item._id]: item.index });
          }

          return acc;
        },
        { list: [], map: {} }
      );

      const { error } = await updateFolderOrder({
        communityId: activeCommunityId,
        payload: formattedData.list
      });

      if (error) throw new Error(error);

      const updatedIndexMap = formattedData.map;
      const updatedFolderList = folderList.map((folder) => {
        const folderId = folder._id;

        if (exists({ value: updatedIndexMap[folderId] }))
          return { ...folder, index: updatedIndexMap[folderId] };

        return folder;
      });
      setFolderList(updatedFolderList);
      showSuccessToast(t('order-saved-to-custom'));
      getFolderList({ communityId: activeCommunity?._id });
      closeFolderReorderModal({ hasChanged: false });
    } catch (e) {
      showToast({ type: 'error', text: e.message });
    }
  };

  const cancelChangesOnFolderItem = useCallback(() => {
    setIsFolderItemModalOpen(false);
  }, []);

  // async (id, params, folderInfo, clientId) => {
  const saveChangesOnFolderItem = useCallback(
    async (id, params, folderItem) => {
      const clientId = folderItem?.clientId;
      setSelectedFolderItem(null);
      setIsFolderItemModalOpen(false);
      const isThumbnailUpdate =
        params?.thumbnail && Object.keys(params).length === 1;
      folderItem = {
        ...folderItem,
        ...params
      };
      if (id) {
        const { data } = await updateCommunityFolderItem({
          communityId: activeCommunityId,
          folderItemId: id,
          payload: folderItem
        });
        let updatedDated = {
          ...data,
          clientId
        };
        if (data && clientId) {
          // check if the update item is inside a section.
          const parentSectionId = data.parentSectionId;
          const isFolderItemInSection = !!parentSectionId;

          let updatedFolderItemList = [...allFolderItemsList];

          // If updated folder item is inside a section
          if (isFolderItemInSection) {
            // Get the parent section
            const parentFolderItemIndex = allFolderItemsList.findIndex(
              (folderItem) => folderItem._id === parentSectionId
            );

            if (parentFolderItemIndex === -1) return;

            // update the item inside section.items array
            updatedFolderItemList[parentFolderItemIndex].items =
              updatedFolderItemList[parentFolderItemIndex].items.map(
                (folderItemInSection) => {
                  if (folderItemInSection.clientId === clientId) {
                    return {
                      ...updatedDated,
                      clientId,
                      isDeleted: false,
                      isEditing: false,
                      isChanged: false
                    };
                  }
                  return folderItemInSection;
                }
              );
            setFolderItemsList(updatedFolderItemList);
          }

          // if updated folder item is at root level
          if (clientId && !isFolderItemInSection) {
            if (folderItem.type === FOLDER_ITEM_TYPE_SECTION) {
              updatedDated.items = JSON.parse(
                JSON.stringify(folderItem.items)
              );
            }
            updatedFolderItemList = allFolderItemsList.map((item) => {
              if (item.clientId === clientId) {
                return {
                  ...updatedDated,
                  clientId,
                  isDeleted: false,
                  isEditing: false,
                  isChanged: false
                };
              }
              return item;
            });
            setFolderItemsList(updatedFolderItemList);
          }
        }
      }

      showToast({
        text: isThumbnailUpdate
          ? t('thumbnail-image-edited')
          : t('item-saved'),
        nplVersion: 2
      });
    },
    [activeCommunityId, allFolderItemsList]
  );

  const cancelChangesForFolderItems = useCallback(() => {
    setShowSaveChangesOption(false);
  }, []);

  // throttling the progress bar to stop it from re rendering too many times for a small change in the progress
  useEffect(() => {
    if (
      !isThrottledProgress &&
      status === STATUS.UPLOADING &&
      Object.keys(uploadProgressMap).length > 0
    ) {
      setThrottledUploadingProgressMap(uploadProgressMap);
      setIsThrottledProgress(true);

      setTimeout(() => {
        setIsThrottledProgress(false);
      }, 1500); // Adjust the interval as per your requirements
    }
  }, [isThrottledProgress, status, uploadProgressMap]);

  // useEffect(() => {
  //   if (selectedFolder) setCurrentFolderState(selectedFolder);
  // }, [selectedFolder]);

  useEffect(() => {
    if (currentRouterQuery?.addProduct) {
      openCreateFolderSideBar('new');
    }
  }, [currentRouterQuery?.addProduct]);

  const value = {
    activeCommunity,
    activeTabId,
    setActiveTabId,

    allFolderItemsList,
    sectionFolderItemsList,
    filesFolderItemsList,
    isFolderEmpty,

    isFolderExecutionLoading,
    classFolderList,
    collectionFolderList,
    isFolderListLoading,

    isFolderCreationModalOpen,
    selectedFolder,
    currentFolderState,
    setCurrentFolderState,
    selectSortState,
    folderStateChanged,
    onFolderFormFieldChange,
    onFolderModalSubmit,
    isClassConfirmationModalOpen,
    setIsFolderConfirmationModalOpen,
    onFolderModalCloseConfirm,
    isConfirmBackModalOpen,
    setIsConfirmBackModalOpen,
    onFolderEdit,
    folderFormErrors,

    isFolderModalOpen,
    closeFolderModal,
    openCreateFolderSideBar,

    isFolderDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    onFolderDelete,
    onFolderUnpublish,
    onFolderPublish,
    closeDeleteItemModal,
    openDeleteItemModal,
    isFolderItemDeleteModalOpen,
    handlerDeleteFolderItem,
    // folder item
    isFolderItemsLoading,
    createSectionFolderItem,
    onAddItemsToFolderList,
    onFolderItemsDrop,
    filesErrorList,
    clearFilesErrorList,
    folderItemIdsInFocus,
    removeFolderItemFromFocus,
    removeAllFolderItemsFromFocus,
    activeFolderItemTabId,
    handeChangeFolderTabId,

    onDndDragEnd,

    discardAllChanges,
    goToFolderEditScreen,

    status,
    filesUploadingStatus,
    resetUploadedStatus,

    getFolderDetails,
    getFolderItemsInfo,
    folderList,
    onSearchChange,
    searchTerm,
    filteredFolderList,
    getFolderList,
    sortState,
    currency,
    setSortState,
    activeCommunityId,
    resetDataState,
    isFolderReorderModalOpen,
    openFolderReorderModal,
    closeFolderReorderModal,
    isFolderReorderDiscardModalOpen,
    setIsFolderReorderDiscardModalOpen,
    onFolderReorder,
    uploadProgressMap: throttledUploadingProgressMap,
    estimatedTimeLeft,
    showAddContentDiv,
    setShowAddContentDiv,
    onFolderItemEditClick,
    selectedFolderItem,
    isFolderItemModalOpen,
    cancelChangesOnFolderItem,
    saveChangesOnFolderItem,
    showSaveChangesOption,
    cancelChangesForFolderItems,
    openShareModal,
    shortUrl,
    showShareModal,
    setShowShareModal,
    setPaidModalSelected,
    paidModalSelected,
    onFolderCardClick,
    onDiscountCreationSubmit,
    closeDiscountCreationModalFromDropdown,
    onDiscountCreationSubmitFromDropdown,
    openDiscountCreationModalFromDropdown,
    showDiscountCreationModalFromDropdown,
    openDiscountCreationModal,
    closeDiscountCreationModal,
    showDiscountCreationModal,
    showDeleteDiscountModal,
    closeDeleteDiscountModal,
    onDiscountDeleteClick,
    onDiscountDeleteModalConfirm,
    selectedDiscountForDeletion,
    selectedEventForDiscountCreation,
    routeToCreateSession
  };

  return (
    <LibraryContext.Provider value={value}>
      {children}
    </LibraryContext.Provider>
  );
};

export const useLibrarySetupContext = () => useContext(LibraryContext);
